import React, { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'
import Dropdown from 'react-bootstrap/Dropdown'
import styled from 'styled-components/macro'
import { CountChip } from '../../Chips/BasicChip'
import { useNavigateContext } from '../../Navigation/NavigateContext'
import {
  FilterDropdownToggle,
  FilterDropdownToggleContent,
} from './FilterBar.styles'
import {
  DropdownMenuItemStyle,
  DropdownMenuStyle,
} from './FilterDropdowns/FilterDropdown.styles'

const MoreFiltersMenu = styled.div`
  ${DropdownMenuStyle}
  position: absolute;
  display: flex;
  flex-direction: column;

  .filter-item {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    margin: 4px;

    .dropdown {
      padding: 2px 0;

      .dropdown-toggle {
        ${DropdownMenuItemStyle}
      }

      &.show {
        > .dropdown-menu {
          position: absolute !important;
          left: 100% !important;
          top: 0 !important;
          margin-top: 4px;
          height: auto;
          overflow: auto;
          z-index: 1060;
          margin-bottom: 8px !important;
          transform: none !important;
        }
      }
    }
  }
`

const MoreFiltersTopMenu = styled(Dropdown)`
  &.show {
    > .dropdown-menu {
      overflow: visible;
      visibility: visible;
    }
  }
  > .dropdown-menu {
    overflow: hidden;
    visibility: hidden;
  }
`

interface MoreFiltersDropdownProps {
  filters: {
    id: string | string[]
    element: React.ReactNode
  }[]
}

export const MoreFiltersDropdown = ({
  filters,
}: MoreFiltersDropdownProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)
  const location = useLocation()
  const {
    navigateState: { parsedQueryParams },
  } = useNavigateContext()
  const searchParamsFallback = useMemo(() => {
    return queryString.parse(location.search, {
      arrayFormat: 'bracket',
    })
  }, [location])
  const searchParams = parsedQueryParams || searchParamsFallback

  const numSelections = useMemo(() => {
    return Object.keys(searchParams).reduce((acc, curr) => {
      if (
        !filters.some((filter) => {
          if (Array.isArray(filter.id)) {
            return filter.id.includes(curr)
          }
          return filter.id === curr
        })
      ) {
        return acc
      }
      if (Array.isArray(searchParams[curr])) {
        return acc + (searchParams[curr]?.length || 0)
      }
      return acc + 1
    }, 0)
  }, [searchParams, filters])

  const handleToggle = (show: boolean) => {
    setIsOpen(show)
  }

  const filtersElements = useMemo(
    () =>
      filters.map((filter) => (
        <div key={getFilterId(filter)} className="filter-item">
          {filter.element}
        </div>
      )),
    [filters],
  )

  return (
    <MoreFiltersTopMenu
      autoClose="outside"
      show={isOpen}
      onToggle={handleToggle}
    >
      <Dropdown.Toggle
        as={FilterDropdownToggle}
        id="more-filters-dropdown"
        $hasSelection={numSelections > 0}
        $isOverflow={true}
      >
        <FilterDropdownToggleContent>
          <p>More filters</p>
          {numSelections > 0 && <CountChip ml="xxs">{numSelections}</CountChip>}
        </FilterDropdownToggleContent>
      </Dropdown.Toggle>
      <Dropdown.Menu as={MoreFiltersMenu}>{filtersElements}</Dropdown.Menu>
    </MoreFiltersTopMenu>
  )
}

const getFilterId = (filter: { id: string | string[] }): string => {
  if (Array.isArray(filter.id)) {
    return filter.id.join(',')
  }
  return filter.id
}
